import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/**
 * PageBreadcrumb — fil d'Ariane éditorial mobile-first.
 *
 * Usage :
 *   <PageBreadcrumb items={[
 *     { label: 'Ressources', to: '/ressources-officielles' },
 *     { label: 'Mentions légales' }, // dernier = actif (pas de to)
 *   ]} />
 *
 * - Toujours préfixé par l'icône "Accueil" → /
 * - Mobile : typographie 11 px, tracking large, séparateur en chevron fin
 * - Respecte la hiérarchie SEO (nav + aria-label + aria-current)
 * - Gère automatiquement l'overflow horizontal sur petits écrans
 */
export function PageBreadcrumb({ items = [], className = '' }) {
  if (!items || items.length === 0) return null;

  return (
    <nav
      aria-label="Fil d'Ariane"
      data-testid="page-breadcrumb"
      className={`w-full overflow-x-auto scrollbar-none -mx-1 px-1 ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      <ol className="flex items-center gap-1.5 whitespace-nowrap text-[11px] sm:text-xs font-semibold tracking-wide">
        <li>
          <Link
            to="/"
            aria-label="Retour à l'accueil"
            className="inline-flex items-center gap-1 text-foreground/60 hover:text-[hsl(var(--primary))] transition-colors"
            data-testid="breadcrumb-home"
          >
            <Home className="w-3.5 h-3.5" />
            <span className="sr-only sm:not-sr-only">Accueil</span>
          </Link>
        </li>
        {items.map((it, i) => {
          const isLast = i === items.length - 1;
          return (
            <React.Fragment key={i}>
              <li aria-hidden="true" className="text-foreground/30">
                <ChevronRight className="w-3 h-3" />
              </li>
              <li>
                {isLast || !it.to ? (
                  <span
                    aria-current="page"
                    className="text-foreground/90 font-bold"
                    data-testid={`breadcrumb-current`}
                  >
                    {it.label}
                  </span>
                ) : (
                  <Link
                    to={it.to}
                    className="text-foreground/60 hover:text-[hsl(var(--primary))] transition-colors"
                    data-testid={`breadcrumb-link-${i}`}
                  >
                    {it.label}
                  </Link>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

export default PageBreadcrumb;
